'use client';

import Script from 'next/script';
import { useEffect, useRef } from 'react';

// Trustpilot's bootstrap script attaches this global once loaded.
declare global {
  interface Window {
    Trustpilot?: {
      loadFromElement: (el: HTMLElement | null, forceReload?: boolean) => void;
    };
  }
}

interface TrustpilotBadgeProps {
  /** Trustpilot business unit id (from the widget code). */
  businessUnitId?: string;
  /** Widget template id. Default = Micro Combo. */
  templateId?: string;
  /** Public profile URL the badge links to. */
  href?: string;
  locale?: string;
  /** Optional invitation token from the widget code. */
  token?: string;
  height?: string;
  width?: string;
  theme?: 'light' | 'dark';
  className?: string;
}

export default function TrustpilotBadge({
  businessUnitId = '6772e2b03a025c23cd02b484',
  templateId = '5419b637fa0340045cd0c936', // Micro TrustScore
  href = 'https://www.trustpilot.com/review/luxeskies.com',
  locale = 'en-US',
  token = 'da591daf-406c-490b-97d7-e3ae758474e9',
  height = '20px',
  width = '100%',
  theme = 'light',
  className,
}: TrustpilotBadgeProps) {
  const ref = useRef<HTMLDivElement>(null);

  // Render/refresh the widget. Trustpilot's script only auto-inits on first
  // page load, so we must call loadFromElement ourselves to cover SPA
  // navigation (the component remounts but the script is already present).
  const render = () => {
    if (typeof window !== 'undefined' && window.Trustpilot && ref.current) {
      window.Trustpilot.loadFromElement(ref.current, true);
    }
  };

  useEffect(() => {
    render();
  }, []);

  return (
    <>
      <Script
        src="https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
        strategy="afterInteractive"
        onLoad={render}
      />
      <div
        ref={ref}
        className={`trustpilot-widget ${className ?? ''}`}
        data-locale={locale}
        data-template-id={templateId}
        data-businessunit-id={businessUnitId}
        data-style-height={height}
        data-style-width={width}
        data-theme={theme}
        data-token={token}
      >
        <a href={href} target="_blank" rel="noopener noreferrer">
          Trustpilot
        </a>
      </div>
    </>
  );
}
